import React, {
  useEffect,
  useState,
  useCallback,
  memo,
  useMemo,
  Suspense
} from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { Modal } from "@tvg/design-system/web";
import mediator from "@tvg/mediator";
import styled from "styled-components";
import {
  closeMybetsPastPerformance,
  closeMyBetsStandalone
} from "@tvg/sh-lib-my-bets/redux/actions";
import { getHasMyBetsPastPerformanceBack } from "@tvg/sh-lib-my-bets/redux/selectors";
import { useNavigate } from "@tvg/custom-hooks";
import { getIsLogged } from "@urp/store-selectors";
import { getDeviceOS } from "@tvg/sh-utils/isSafari";
import { breakpoints, useMediaQuery } from "@tvg/design-system/src";
import { store } from "@tvg/sh-lib-my-bets/redux/store";
import { SettledTabEnum } from "@tvg/sh-lib-my-bets/utils/types";
import tvgConf from "@tvg/conf";
import type { Props } from "./types";

type RootState = ReturnType<typeof store.getState>;

const MyBetsModalHeader = React.lazy(() => import("./header"));
const MyBetsTopStickyArea = React.lazy(() => import("./topStickyArea"));

const LoadingContainer = styled.div`
  height: 100px;
`;

const LoadingFallback = memo(() => <LoadingContainer />);
LoadingFallback.displayName = "LoadingFallback";

const MyBetsModal = memo(
  ({
    currentPath,
    isPastPerformanceOpen,
    isVideoShown,
    title,
    selectedTab,
    selectedSettledTab,
    qaLabel,
    isOpen,
    children,
    onScroll,
    onRender
  }: Props) => {
    const isMobile = useMediaQuery(breakpoints.tablet.max.sm);
    const dispatch = useDispatch();
    const [enableRender, setEnableRender] = useState(false);

    const { isLogged, hasPastPerformanceBack } = useSelector<
      RootState,
      { isLogged: boolean; hasPastPerformanceBack: SettledTabEnum }
    >(
      (state) => ({
        isLogged: getIsLogged(state),
        hasPastPerformanceBack: getHasMyBetsPastPerformanceBack(state)
      }),
      shallowEqual
    );

    const history = useNavigate();

    const closeMyBetsModal = useCallback(
      (overlayType: string = "") => {
        history.push(currentPath);
        dispatch(closeMyBetsStandalone());
        if (overlayType !== "none") {
          mediator.base.dispatch({
            type: "MYBETS_CLICKS_X_BUTTON",
            payload: {
              selectedTab,
              selectedSettledTab
            }
          });
        }
      },
      [currentPath, dispatch, history, selectedTab, selectedSettledTab]
    );

    const handleClose = useCallback(() => {
      closeMyBetsModal("none");
      setEnableRender(false);
    }, [closeMyBetsModal]);

    const handleBack = useCallback(() => {
      dispatch(closeMybetsPastPerformance());
    }, [dispatch]);

    const Header = useMemo(
      () => (
        <Suspense fallback={<LoadingFallback />}>
          <MyBetsModalHeader
            title={title}
            {...(hasPastPerformanceBack &&
              isPastPerformanceOpen && {
                onBack: handleBack
              })}
            onClose={handleClose}
          />
        </Suspense>
      ),
      [
        title,
        hasPastPerformanceBack,
        isPastPerformanceOpen,
        handleBack,
        handleClose
      ]
    );

    const TopStickyArea = useMemo(
      () => (
        <Suspense fallback={<LoadingFallback />}>
          <MyBetsTopStickyArea
            isPastPerformanceOpen={isPastPerformanceOpen}
            showBannerFeedback={!isVideoShown}
          />
        </Suspense>
      ),
      [isPastPerformanceOpen, isVideoShown]
    );

    const insetValue = useMemo(
      () =>
        isMobile &&
        getDeviceOS() === "iOS" &&
        typeof window !== "undefined" &&
        tvgConf().product === "fdrmobile"
          ? 120
          : undefined,
      [isMobile]
    );

    useEffect(() => {
      if (isOpen && !isLogged) {
        closeMyBetsModal();
      }
    }, [isOpen, isLogged, closeMyBetsModal]);

    const handleOpen = useCallback(() => {
      const timer = setTimeout(() => setEnableRender(true), 300);
      return () => clearTimeout(timer);
    }, []);

    return (
      <Modal
        customHeader={Header}
        onClose={closeMyBetsModal}
        isOpen={isOpen || isPastPerformanceOpen}
        qaLabel={qaLabel}
        stickyAreaExtraContent={TopStickyArea}
        p={0}
        maxHeight={isVideoShown ? "68%" : undefined}
        onScroll={onScroll}
        onRender={onRender}
        hasMaxHeight={true}
        onOpen={handleOpen}
        overrideInset={insetValue}
      >
        {enableRender ? children : null}
      </Modal>
    );
  }
);

MyBetsModal.displayName = "MyBetsModal";

export default MyBetsModal;
